import React from 'react';
import { Heading } from '../../components/guide/Heading';
import HeroBanner from '../../components/guide/HeroBanner';
import BannerImg from '../../assets/images/banner/guide_banner_2.png';
import CtaBanner from '../../components/guide/CtaBanner';
import AdProcessImg from '../../assets/images/ad/ad_process.svg';
import { cn } from '../../utils/helper';
import HeaderPath from "../../components/common/HeaderPath";

type ProcessStep = {
  id: number;
  title: string;
  points: string[];
  highlight?: boolean;
};

const PROCESS_STEPS: ProcessStep[] = [
  {
    id: 1,
    title: '현장 분석',
    points: ['· 입지 및 분양가 분석', '· 경쟁사 분석', '· 목표 및 전략 방향 설정'],
  },
  {
    id: 2,
    title: '광고 컨설 회의',
    points: ['· 전달된 예산 및 전략 논의', '· 현장 맞춤 실행 확정', ''],
  },
  {
    id: 3,
    title: '홈페이지·배너 제작',
    points: ['· 플랫폼 최적화 디자인', '· 모바일/PC 랜딩페이지', '· 사용자 경험 강화'],
  },
  {
    id: 4,
    title: '광고 송출',
    points: ['· 3대 주요 매체 송출', '· 자체 키워드 하이타겟', '· 정밀 타겟팅 및 리타겟팅'],
  },
  {
    id: 5,
    title: '실시간 광고 관리',
    points: ['· 실시간 모니터링', '· 비용 즉시 조정', '· 고성과 영역 확대'],
  },
  {
    id: 6,
    title: '성과 검증 및 보고',
    points: ['· 광고 성과 투명 제공', '· 일일 보고서 제공', '· 광고 보고서 제공'],
    highlight: true,
  },
];

const ProcessGuidePage: React.FC = () => (
  <section className="w-full bg-white">
    <div className="mx-auto w-full max-w-[1072px] px-0 sm:px-6 lg:px-0">
      <HeaderPath path={'> 광고 운영 프로세스'}/>
      <HeroBanner image={BannerImg} message="미분양 브랜딩부터, 현장 완판까지!" />
      <Heading className="hidden lg:block lg:mt-[93px] lg:mb-[96px]" as="h1" size="h1">
        광고 운영 프로세스
      </Heading>
      <div className="mt-0 flex flex-col gap-12 sm:gap-16 lg:mt-0 lg:gap-[93px]">
        <div>
          <h2 className="mb-5 text-[20px] font-[700] text-black sm:mb-[19px] sm:text-[25px]">1. 운영 원칙</h2>
          <Heading as="p" size="subtitle" className="text-black font-[400] leading-relaxed">
            분양의신은 단순히 광고를 집행하는 것이 아니라
            현장의 목표 달성을 중심에 두고 운영합니다. 광고 기획부터 디자인, 집행, 성과 보고 및 관리까지{' '}
            전 과정을 직접 수행하여 성과와 비용을 동시에 관리합니다.
          </Heading>
          <Heading as="p" size="subtitle" className="mt-[20px] text-black font-[600] leading-relaxed">
            “고객님이 기본 자료만 전달해 주시면, <br className="block md:hidden" />
            나머지는 전부 분양의신이 알아서 진행합니다.”
          </Heading>
        </div>

        {/* 2. 운영 프로세스 */}
        <section className="w-full">
          <h2 className="mb-5 text-[20px] font-[700] text-black sm:mb-[19px] sm:text-[25px]">2. 운영 프로세스</h2>
          <img className={'block mx-auto lg:hidden'} src={AdProcessImg} alt={''}/>
          <div className="hidden gap-4 lg:flex lg:items-stretch lg:gap-0">
            {PROCESS_STEPS.map((step, index) => {
              const isLast = index === PROCESS_STEPS.length - 1;
              const numberClass = cn(
                'mb-2 inline-flex h-[22px] w-[22px] items-center justify-center rounded-full text-sm font-bold',
                step.highlight ? 'bg-white text-slate-900' : 'bg-slate-900 text-white',
              );
              const dividerClass = step.highlight ? 'bg-white/40' : 'bg-slate-300';
              const listClass = cn(
                'space-y-2 text-[13px] leading-none text-left',
                step.highlight ? 'text-[#FFFFFFE5]' : 'text-[#0D0D0DE5]',
              );

              return (
                <React.Fragment key={step.id}>
                  <div
                    className={cn(
                      'relative z-10 flex min-h-[170px] flex-1 flex-col items-center justify-center rounded-2xl border px-[9px] py-[22px] text-center',
                      step.highlight ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-200 bg-white text-slate-900',
                    )}
                  >
                    <div className={numberClass}>{step.id}</div>
                    <h3 className="text-base font-semibold">{step.title}</h3>
                    <span className={cn('mx-auto my-3 block h-px w-[27px]', dividerClass)} />
                    <ul className={listClass}>
                      {step.points.map((point, pointIndex) => {
                        if (!point) {
                          return <li key={pointIndex} className="h-[13px]" />;
                        }

                        return <li key={pointIndex}>{point}</li>;
                      })}
                    </ul>
                  </div>
                  {!isLast && (
                    <div className="hidden items-center lg:flex" aria-hidden="true">
                      <span className="block h-px w-4 bg-[#E0E4E8]" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </section>

        <div>
          <h2 className="mb-5 text-[20px] font-[700] text-black sm:mb-[19px] sm:text-[25px]">3. 분양의신 차별화 포인트</h2>
          <div
            className="mt-5"
            style={{
              fontFamily:
                'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Noto Sans, Helvetica, Arial, Apple Color Emoji, Segoe UI Emoji',
            }}
          >
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center text-sm font-normal leading-tight tracking-[0] sm:text-[16px]">
                <thead>
                  <tr>
                    <th className="border border-[#E0E4E8] bg-[#343A40] px-[12px] py-[10px] text-left text-white">구분</th>
                    <th className="border border-[#E0E4E8] bg-[#343A40] px-[12px] py-[10px] text-white">설명</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="align-middle">
                    <td className="border border-[#E0E4E8] bg-[#F1F3F5] px-[12px] py-[10px] text-left">
                      <span className="font-[600]">원스톱 서비스</span>
                    </td>
                    <td className="border border-[#E0E4E8] bg-white px-[12px] py-[10px] text-left">
                      기획 → 제작 → 집행 → 관리까지 <br className="block md:hidden" />
                      모든 과정을 직접 수행 및 보고
                    </td>
                  </tr>
                  <tr className="align-middle">
                    <td className="border border-[#E0E4E8] bg-[#F1F3F5] px-[12px] py-[10px] text-left">
                      <span className="font-[600]">전환 중심 설계</span>
                    </td>
                    <td className="border border-[#E0E4E8] bg-white px-[12px] py-[10px] text-left">
                      단순 노출이 아닌 <br className="block md:hidden" />
                      실제 상담·계약으로 이어지는 광고
                    </td>
                  </tr>
                  <tr className="align-middle">
                    <td className="border border-[#E0E4E8] bg-[#F1F3F5] px-[12px] py-[10px] text-left">
                      <span className="font-[600]">하이타겟 기술</span>
                    </td>
                    <td className="border border-[#E0E4E8] bg-white px-[12px] py-[10px] text-left">
                      업계 유일 자체 타겟팅 시스템으로 <br className="block md:hidden" />
                      초정밀 타겟팅·강력한 리타겟팅 제공
                    </td>
                  </tr>
                  <tr className="align-middle">
                    <td className="border border-[#E0E4E8] bg-[#F1F3F5] px-[12px] py-[10px] text-left">
                      <span className="font-[600]">
                        업계 최초 <br className="block md:hidden" />
                        아트디자인팀 보유
                      </span>
                    </td>
                    <td className="border border-[#E0E4E8] bg-white px-[12px] py-[10px] text-left">
                      <span className="block md:hidden">
                        시각적 차별화로 몰입 유도
                        <br />
                        몰입아트·미디어아트 홈페이지
                        <br />
                        프리미엄 콘텐츠 제작
                      </span>
                      <span className="hidden md:block">
                        시각적 차별화로 몰입 유도, 몰입아트·미디어아트 홈페이지·프리미엄 콘텐츠 제작
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <CtaBanner
          message="처음부터 끝까지, \n고객님만을 위한 전담팀이 필요하신가요?"
          buttonText="광고문의 바로가기 →"
          href="https://분양의신.net/contact"
        />
      </div>
    </div>
  </section>
);

export default ProcessGuidePage;
